import { Fragment, type ReactNode } from "react";

// Minimal markdown→React renderer purpose-built for the mascot chat:
// the assistant's replies use bold, inline code, fenced code, bullet
// and numbered lists, occasional small headings, and links. We avoid
// pulling a full library because the surface area we care about is
// tiny and unsanitised innerHTML on AI-generated text would be a
// gratuitous XSS hazard.
//
// Everything goes through React's text rendering, so HTML in the
// source is rendered as literal text — there is no `dangerouslySet`
// path here.

type Inline = ReactNode;

function renderInline(line: string): Inline[] {
  const out: Inline[] = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf.length > 0) {
      out.push(buf);
      buf = "";
    }
  };
  while (i < line.length) {
    const c = line[i];
    // Inline code: `text`
    if (c === "`") {
      const end = line.indexOf("`", i + 1);
      if (end === -1) {
        buf += c;
        i++;
        continue;
      }
      flush();
      out.push(<code key={out.length}>{line.slice(i + 1, end)}</code>);
      i = end + 1;
      continue;
    }
    // Bold: **text**
    if (c === "*" && line[i + 1] === "*") {
      const end = line.indexOf("**", i + 2);
      if (end === -1) {
        buf += c;
        i++;
        continue;
      }
      flush();
      out.push(
        <strong key={out.length}>{renderInline(line.slice(i + 2, end))}</strong>,
      );
      i = end + 2;
      continue;
    }
    // Italic: *text* (single star) — only treat as italic if there's
    // a non-space char right after and a closing star on the line.
    if (c === "*" && line[i + 1] !== "*" && line[i + 1] !== " ") {
      const end = line.indexOf("*", i + 1);
      if (end === -1) {
        buf += c;
        i++;
        continue;
      }
      flush();
      out.push(<em key={out.length}>{renderInline(line.slice(i + 1, end))}</em>);
      i = end + 1;
      continue;
    }
    // Link: [text](url)
    if (c === "[") {
      const close = line.indexOf("]", i + 1);
      if (close === -1 || line[close + 1] !== "(") {
        buf += c;
        i++;
        continue;
      }
      const urlClose = line.indexOf(")", close + 2);
      if (urlClose === -1) {
        buf += c;
        i++;
        continue;
      }
      const text = line.slice(i + 1, close);
      const url = line.slice(close + 2, urlClose);
      flush();
      out.push(
        <a
          key={out.length}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>,
      );
      i = urlClose + 1;
      continue;
    }
    buf += c;
    i++;
  }
  flush();
  return out;
}

const HEADING_RE = /^(#{1,3})\s+(.+)$/;
const ULIST_RE = /^\s*[-*]\s+(.+)$/;
const OLIST_RE = /^\s*\d+\.\s+(.+)$/;

export function renderMarkdown(source: string): ReactNode {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Fenced code block: ```lang ... ```
    if (line.startsWith("```")) {
      const start = i + 1;
      let end = start;
      while (end < lines.length && !lines[end].startsWith("```")) end++;
      blocks.push(
        <pre key={key++} className="md-pre">
          <code>{lines.slice(start, end).join("\n")}</code>
        </pre>,
      );
      i = end + 1;
      continue;
    }

    // Heading: #, ##, ###
    const head = HEADING_RE.exec(line);
    if (head) {
      // Render small even at h1 — chat bubble is narrow.
      blocks.push(
        <h4 key={key++} className={`md-h md-h${head[1].length}`}>
          {renderInline(head[2])}
        </h4>,
      );
      i++;
      continue;
    }

    // Unordered list
    if (ULIST_RE.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length) {
        const m = ULIST_RE.exec(lines[i]);
        if (!m) break;
        items.push(<li key={items.length}>{renderInline(m[1])}</li>);
        i++;
      }
      blocks.push(
        <ul key={key++} className="md-ul">
          {items}
        </ul>,
      );
      continue;
    }

    // Ordered list
    if (OLIST_RE.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length) {
        const m = OLIST_RE.exec(lines[i]);
        if (!m) break;
        items.push(<li key={items.length}>{renderInline(m[1])}</li>);
        i++;
      }
      blocks.push(
        <ol key={key++} className="md-ol">
          {items}
        </ol>,
      );
      continue;
    }

    // Paragraph: collect contiguous lines that aren't another block kind.
    const paraLines: string[] = [];
    while (
      i < lines.length
      && lines[i].trim() !== ""
      && !lines[i].startsWith("```")
      && !HEADING_RE.test(lines[i])
      && !ULIST_RE.test(lines[i])
      && !OLIST_RE.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="md-p">
        {renderInline(paraLines.join(" "))}
      </p>,
    );
  }
  return (
    <>
      {blocks.map((b, idx) => (
        <Fragment key={idx}>{b}</Fragment>
      ))}
    </>
  );
}
